import { createDynamicFurniture } from "./dynamicFurniture";
import { capitalize } from '@gettactic/helpers/src/strings/capitalize';
import { IconValue } from '../../../components';
import {
  DEFAULT_FONT_FAMILY,
  DEFAULT_NODE_TEXT_COLOR,
  DEFAULT_TEXT_SHAPE_COLOR,
  DEFAULT_TEXT_SHAPE_FONT_SIZE,
  DEFAULT_WALL_COLOR,
  DEFAULT_WALL_THICKNESS,
  DEFAULT_ZONE_BORDER_COLOR,
  DEFAULT_ZONE_BORDER_WIDTH,
  DEFAULT_ZONE_FILL_COLOR,
  DEFAULT_ZONE_FILL_OPACITY
} from "../components/map/defines";
import { FURNITURE_LIST, Furniture } from "../furniture-list";
import {
  Shape,
  SpotLabelPlacement,
  SpotOrientation,
  SpotShapeType,
  WallPathEdge,
  WallPathVertex,
  ZoneType
} from "../types";
import { getRandomColor } from "./colors";
import { generateShapeId } from "./generateShapeId";
import {
  createFurnitureShape,
  createIconShape,
  createImageShape,
  createSvgShape
} from "./svg";

interface ShapePosition {
  x?: number;
  y?: number;
  text?: string;
}

export const createRectangle = (position?: ShapePosition): Shape => ({
  id: generateShapeId("rectangle"),
  type: "rectangle",
  x: position?.x || 0,
  y: position?.y || 0,
  width: 100,
  height: 100,
  color: DEFAULT_WALL_COLOR,
  layerType: "shapes"
});

export const createCircle = (position?: ShapePosition): Shape => ({
  id: generateShapeId("circle"),
  type: "circle",
  x: position?.x || 0,
  y: position?.y || 0,
  width: 100,
  height: 100,
  radius: 50,
  color: DEFAULT_WALL_COLOR,
  layerType: "shapes"
});

export const createText = (position?: ShapePosition): Shape => ({
  id: generateShapeId("text"),
  type: "text",
  x: position?.x || 0,
  y: position?.y || 0,
  width: 200,
  height: 16,
  textColor: DEFAULT_TEXT_SHAPE_COLOR,
  text: "Double click to edit",
  fontSize: DEFAULT_TEXT_SHAPE_FONT_SIZE,
  fontFamily: DEFAULT_FONT_FAMILY,
  isBold: true,
  textAlign: "center",
  layerType: "shapes"
});

export const createIconElement = (
  position?: ShapePosition,
  icon?: IconValue,
  text?: string,
  iconUrl?: string,
  width?: number,
  height?: number,
  svgContent?: string
): Shape => {
  return createIconShape(
    {
      x: position?.x || 0,
      y: position?.y || 0,
      text,
      width,
      height,
      svgContent
    },
    icon || { icon: "Star", set: "lucide" },
    iconUrl
  );
};

export const createFurnitureElement = (
  position?: ShapePosition,
  furniture?: Furniture
): Shape => {
  if (furniture?.dynamicTable) return createDynamicFurniture(furniture.dynamicTable, {x:position?.x||0,y:position?.y||0});
  return createFurnitureShape(
    {
      x: position?.x || 0,
      y: position?.y || 0
    },
    furniture || FURNITURE_LIST[0]
  );
};

export const createSvgElement = (
  position?: ShapePosition,
  path?: string,
  content?: string,
  width?: number,
  height?: number
): Shape =>
  createSvgShape(
    {
      x: position?.x || 0,
      y: position?.y || 0,
      width,
      height
    },
    path,
    content
  );

export const createImageElement = (
  position?: ShapePosition,
  path?: string,
  width?: number,
  height?: number
): Shape =>
  createImageShape(
    {
      x: position?.x || 0,
      y: position?.y || 0,
      width,
      height
    },
    path
  );

export const createZone = (position?: ShapePosition): Shape => ({
  id: generateShapeId("zone"),
  type: "zone",
  x: position?.x || 0,
  y: position?.y || 0,
  width: 200,
  height: 200,
  zoneType: "open_area" as ZoneType,
  name: position?.text || "Zone",
  color: DEFAULT_ZONE_FILL_COLOR,
  borderColor: DEFAULT_ZONE_BORDER_COLOR,
  borderWidth: DEFAULT_ZONE_BORDER_WIDTH,
  fillOpacity: DEFAULT_ZONE_FILL_OPACITY,
  layerType: "shapes"
});

export const createWall = (
  start?: { x: number; y: number },
  end?: { x: number; y: number }
): Shape => ({
  id: generateShapeId("wall"),
  type: "wall",
  x: start?.x || 0,
  y: start?.y || 0,
  x2: end?.x || 100,
  y2: end?.y || 0,
  width: 0,
  height: 0,
  thickness: DEFAULT_WALL_THICKNESS,
  color: DEFAULT_WALL_COLOR,
  layerType: "shapes"
});

export const computeWallPathBounds = (
  vertices: WallPathVertex[]
): { x: number; y: number; width: number; height: number } => {
  const xs = vertices.map((v) => v.x);
  const ys = vertices.map((v) => v.y);
  const minX = Math.min(...xs);
  const minY = Math.min(...ys);
  const maxX = Math.max(...xs);
  const maxY = Math.max(...ys);
  return { x: minX, y: minY, width: maxX - minX, height: maxY - minY };
};

export const createWallPath = (
  vertices: WallPathVertex[],
  options?: { closed?: boolean; edges?: WallPathEdge[] }
): Shape => {
  const closed = options?.closed ?? false;
  const edgeCount = closed ? vertices.length : Math.max(0, vertices.length - 1);
  const edges = options?.edges ?? Array.from({ length: edgeCount }, () => ({}));
  const bounds = computeWallPathBounds(vertices);
  return {
    id: generateShapeId("wall-path"),
    type: "wall-path",
    x: bounds.x,
    y: bounds.y,
    width: bounds.width,
    height: bounds.height,
    vertices,
    edges,
    thickness: DEFAULT_WALL_THICKNESS,
    closed,
    color: DEFAULT_WALL_COLOR,
    lineCapStart: "round",
    lineCapEnd: "round",
    layerType: "shapes"
  };
};

export const createNodeShape = (position?: ShapePosition): Shape => {
  const x = position?.x || 0;
  const y = position?.y || 0;
  const text = position?.text || "";

  const type = "spot";

  return {
    id: generateShapeId(type),
    type,
    spotAppearance: undefined,
    orientation: "top" as SpotOrientation,
    labelPlacement: "on-rect" as SpotLabelPlacement,
    spotType: "desk" as SpotShapeType,
    textAlign: "center",
    x,
    y,
    label: text,
    isBold: true,
    textColor: DEFAULT_NODE_TEXT_COLOR,
    name: `${capitalize(type)} ${text}`,
    width: 100,
    height: 100,
    spacing: 2,
    rowSpacing: 5,
    rowsExtraSpacing: 20,
    layerType: "spots",
    rectProps: {
      cornerRadius: 2,
      opacity: 1
    }
  };
};
